import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useManagedQuery } from 'utils/managedHooks';
import { GET_USER, GET_CUSTOMER_DETAILS } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { FormattedMessage } from 'react-intl';
import { LOYALTY_NUMBER_PREFIX } from 'constants/passengers';
import SearchCustomerForm from './SearchCustomerForm';
import { isLoungeStaff } from 'utils/user';

import { StyledManageClubEurostarContainer, StyledSearchSuggestionsList, StyledSearchByHeader } from './ManageClubEurostar.styles';
import { Column, Row } from 'components/shared/Layout';

const ERROR_TYPE = {
  SEARCH_BY_MEMBERSHIP_NUMBER_ERROR: 'searchByMembershipNumberError',
  SEARCH_BY_EMAIL_ERROR: 'searchByEmailError'
};

const ManageClubEurostar = () => {
  const [membershipNumber, setMembershipNumber] = useState('');
  const [email, setEmail] = useState('');
  const [errorType, setErrorType] = useState('');
  const [noMembershipError, setNoMembershipError] = useState(false);

  const { push, location } = useHistory();
  const {
    data: { me }
  } = useManagedQuery(GET_USER, {
    fetchPolicy: 'cache-only'
  });

  const isLoungeStaffUser = isLoungeStaff(me);
  const [getCustomerInfo, { loading, error: getCustomerError }] = useLazyQuery(GET_CUSTOMER_DETAILS, {
    onCompleted: (data) => {
      const customerInfo = data?.getCustomerDetails;

      if (customerInfo) {
        const loyalty = customerInfo?.profile?.loyalty;
        if (loyalty && loyalty.membershipNumber) {
          if (shouldReturnToPreviousPath) {
            const previousHash = location?.state?.prevHash || '';
            const previousSearch = location?.state?.prevSearch;
            if (previousSearch) {
              push(`${previousPath}${previousSearch}&membershipNumber=${loyalty.membershipNumber}${previousHash}`);
            } else {
              push(`${previousPath}?membershipNumber=${loyalty.membershipNumber}${previousHash}`);
            }

            return null;
          }

          push(`/clubeurostar/${loyalty.membershipNumber}`);

          return null;
        }
        if (!noMembershipError) {
          setNoMembershipError(true);
        }
      }
    }
  });

  if (isLoungeStaffUser ? false : !me.canManageClubEurostar) {
    push('/');

    return null;
  }

  const previousPath = location?.state?.prevPath;
  const shouldReturnToPreviousPath = [
    '/assistance',
    '/extras',
    '/payments',
    '/notes',
    '/refunds',
    '/payment',
    '/refund-methods',
    '/prr-refund',
    '/journeys',
    '/review-booking',
    '/exchange'
  ].some((path) => previousPath?.includes(path));
  const error = getCustomerError || noMembershipError;

  return (
    <StyledManageClubEurostarContainer>
      <h1>
        <FormattedMessage id="clubEurostar.manageLoyaltyAccount" />
      </h1>
      {loading && (
        <Row span={16} height="100vh">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      )}
      {!loading && (
        <>
          {error && (
            <div data-testid="customer-not-found" data-cy="customer-not-found">
              {errorType === ERROR_TYPE.SEARCH_BY_MEMBERSHIP_NUMBER_ERROR && (
                <p>
                  <FormattedMessage id="clubEurostar.notFoundByMembershipNumber" data-cy="customer-not-found-by-membership-number" />:{' '}
                  <strong>
                    {LOYALTY_NUMBER_PREFIX}
                    {membershipNumber}
                  </strong>
                </p>
              )}
              {errorType === ERROR_TYPE.SEARCH_BY_EMAIL_ERROR && (
                <p>
                  <FormattedMessage id="clubEurostar.notFoundByEmail" data-cy="customer-not-found-by-email" />: <strong>{email}</strong>
                </p>
              )}
              <p>
                <strong>
                  <FormattedMessage id="vouchers.searchSuggestionsTitle" />
                </strong>
                :
              </p>
              <StyledSearchSuggestionsList>
                <li>
                  <FormattedMessage id="vouchers.searchSuggestionSpelling" />
                </li>
                <li>
                  <FormattedMessage id="vouchers.searchSuggestionsAlternative" />
                </li>
              </StyledSearchSuggestionsList>
            </div>
          )}
          <StyledSearchByHeader>
            <FormattedMessage id={error ? 'vouchers.searchAgain' : 'vouchers.searchBy'} />:
          </StyledSearchByHeader>
          <SearchCustomerForm
            onSubmitSearch={async (values) => {
              let variables;
              if (values.membershipNumber) {
                setMembershipNumber(values.membershipNumber);
                setErrorType(ERROR_TYPE.SEARCH_BY_MEMBERSHIP_NUMBER_ERROR);

                variables = {
                  membershipNumber: `${LOYALTY_NUMBER_PREFIX}${values.membershipNumber}`
                };
              } else {
                setEmail(values.email);
                setErrorType(ERROR_TYPE.SEARCH_BY_EMAIL_ERROR);

                variables = {
                  email: values.email
                };
              }

              return getCustomerInfo({
                variables
              });
            }}
          />
        </>
      )}
    </StyledManageClubEurostarContainer>
  );
};

export default ManageClubEurostar;
